/**
 * CAPA 2 — red de seguridad.
 *
 * Barrido recursivo que redacta PII de cualquier payload antes de que salga
 * hacia Sentry. Es la segunda mitad del patrón de dos capas:
 *
 *   Capa 1 (origen)  → `reduxSentry.js` no deja SALIR el estado de Redux.
 *   Capa 2 (red)     → este módulo redacta lo que igual se coló, venga de
 *                      donde venga (contexts, breadcrumbs, extra, tags).
 *
 * Por qué hacen falta las dos: en origen siempre se cuela un campo nuevo. El
 * día que alguien agregue `conversations[].clientRFC` la capa 1 ya no lo
 * enumera (es allowlist, no denylist) y la capa 2 lo redacta igual por patrón.
 *
 * Diseño:
 *  - Redacta por NOMBRE DE CLAVE (denylist) → borra el valor completo, sea
 *    escalar, objeto o array. Cubre el caso "el teléfono está anidado tres
 *    niveles bajo `client`".
 *  - Redacta por PATRÓN DE VALOR (teléfono / email) dentro de cualquier
 *    string → cubre el campo que nadie anticipó y el teléfono incrustado en
 *    una frase o en una URL.
 *  - Redacta CLAVES que son un teléfono → cubre mapas indexados por número
 *    (`{ "5215544332211": {...} }`).
 *  - Acotado en coste: tope de nodos y de profundidad, y WeakSet anti-ciclo.
 *    Corre en el hilo del navegador del asesor; no puede colgar la pestaña.
 */

export const REDACTED = "[Filtered]";
export const TRUNCATED = "[Truncated]";

/** Tope de nodos visitados por evento. Un state de Redux grande no puede
 *  costar más que esto en el hilo principal. */
export const MAX_NODES = 5000;
/** Tope de profundidad. Más allá, Sentry ya no renderiza nada útil. */
export const MAX_DEPTH = 12;

/**
 * Claves cuyo VALOR se borra entero, sin mirarlo. Comparación en minúsculas y
 * sin acentos, y por SUBCADENA — así `clientPhone`, `phoneNumber`, `telefono_2`
 * y `contactoNombre` caen todos sin tener que enumerar variantes.
 *
 * Aquí solo van partes que NO aparecen dentro de palabras corrientes. Las que
 * sí (`text`, `body`, `name`, `nota`) viven en `SENSITIVE_KEY_WORDS`.
 */
const SENSITIVE_KEY_PARTS = [
  // identidad de la persona
  "phone", "telefono", "msisdn", "celular", "cellphone", "mobile", "whatsapp",
  "nombre", "apellido", "lastname", "firstname", "fullname",
  "email", "correo", "mail",
  "address", "direccion", "rfc", "curp",
  // contenido de la conversación.
  // OJO: aquí NO va `message` a secas. Uno de los 24 ducks del store se llama
  // `messages`, y una denylist por subcadena lo borraría entero del resumen —
  // perderíamos `messages.fetching`, que es justo lo que se mira cuando un
  // asesor reporta "no me llegan los mensajes". El CONTENIDO sigue cubierto
  // por `text`, `body`, `caption` y `content`, que son los campos donde vive.
  "messagebody", "lastmessage", "messagetext",
  "caption", "content", "comment", "note",
  // credenciales
  "token", "password", "contrasena", "apikey", "api_key", "secret",
  "authorization", "cookie",
];

/**
 * Claves sensibles que se comparan por PALABRA, no por subcadena.
 *
 * El comentario de arriba razona por qué `message` no puede ir por subcadena
 * (se comería el duck `messages`). Ese mismo razonamiento aplica a estas
 * cuatro, y no se había aplicado — lo señalaron @arturogaro y
 * @kirtashGuerrero27 en la revisión del #230, con la lista exacta de lo que
 * rompían:
 *
 *   `text` → context, requestContext, chatContext, errorContext, contextMenu
 *   `name` → filename, channelName, username, hostname, lastname
 *   `body` → bodyClass
 *   `nota` → notation
 *
 * Borrar `contexts.<algo>.requestContext` entero no protege a nadie y sí deja
 * ciego al que investiga: `Sentry.setContext('requestContext', {endpoint,
 * status})` es un patrón razonable y perdería el diagnóstico completo.
 *
 * La comparación por palabra parte la clave en los límites camelCase y en los
 * separadores (`_`, `-`, `.`), así que:
 *   `text`, `messageText`, `message_text`, `texts`  → sensibles
 *   `context`, `requestContext`, `contextMenu`      → NO
 *   `name`, `clientName`, `nameFromChannel`         → sensibles
 *   `filename`, `hostname`, `username`, `notation`  → NO
 *
 * Las formas todo-en-minúsculas que SÍ son PII (`lastname`, `firstname`,
 * `fullname`, `messagetext`, `messagebody`) siguen cubiertas por subcadena
 * arriba, porque ahí no hay límite de palabra que detectar.
 */
const SENSITIVE_KEY_WORDS = ["text", "body", "name", "nota"];

/**
 * Claves EXACTAS que la regla por palabra marcaría y no debe: son nombres de
 * cosas, no de personas. Se comparan contra la clave normalizada completa —
 * nunca por subcadena— para que `customerName` o `senderNameText` sigan
 * cayendo.
 *
 * Por qué una allowlist corta en vez de bajar `name` a clave exacta: en el
 * store de este dashboard `name` es ambiguo de verdad (el mismo campo lleva
 * "Anderson Cordoba" en `agent` y "VENTAS" en `tag`), así que el default
 * seguro tiene que ser redactar. Lo que se enumera aquí es la excepción
 * verificada, y el día que aparezca un `clienteName` nuevo cae solo.
 */
const TECHNICAL_KEY_EXACT = new Set([
  "operationname", "componentname", "eventname", "fieldname", "typename",
  "__typename", "classname", "tagname", "routename", "actionname", "queryname",
  "modulename", "packagename", "hostname", "filename", "pathname", "username",
  "channelname", "browsername", "osname", "sdkname", "bodyclass",
]);

/**
 * Rutas de primer nivel que NO se tocan: son metadatos técnicos de Sentry sin
 * PII, y redactarlos rompería el diagnóstico (trace ids, versión del SDK).
 * Se comparan contra la primera clave del camino.
 *
 * La exención es de ÁMBITO: solo vale cuando lo que se barre es
 * `event.contexts`, que es donde `device`/`os`/`browser` son de verdad los
 * contextos del SDK. Hay que pedirla con `{ technicalRoots: true }`.
 */
const TECHNICAL_ROOTS = new Set(["trace", "sdk", "runtime", "browser", "os", "device", "react"]);

/** Email / JID de WhatsApp (`5215544332211@c.us` también cae aquí). */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Candidato a teléfono: arranca en dígito (con `+` opcional), admite espacios,
 * guiones, puntos y paréntesis, y termina en dígito.
 *
 * Las fronteras `(^|[^A-Za-z0-9])` y `(?![A-Za-z0-9])` evitan destrozar
 * identificadores alfanuméricos: en el ObjectId `5ed824990274390007afcc70` la
 * corrida `824990274390007` viene precedida por `d`, así que no matchea.
 * Deliberadamente NO usamos lookbehind: Safari < 16.4 no lo soporta y el
 * dashboard se sirve a navegadores de asesores que no controlamos.
 */
const PHONE_CANDIDATE_RE = /(^|[^A-Za-z0-9])(\+?\d[\d\s().-]{5,}\d)(?![A-Za-z0-9])/g;

/**
 * Secretos que viajan como parámetro de URL.
 *
 * No es hipotético: `views/magic-link/MagicLink.view.jsx` lee el JWT de
 * `location.search` y lo usa en `fetch('/auth/magic?token=…')`. Como
 * `httpContextIntegration` de `@sentry/browser` 8.55 copia `location.href` a
 * `event.request.url` —verificado en el fuente: solo setea `url`, `Referer` y
 * `User-Agent`, nunca `query_string`, que es de `@sentry/node`— ese token de
 * sesión llega hoy a Sentry entero, y otra vez en el breadcrumb del fetch.
 * `redactText` solo cubría teléfonos y correos. Lo señaló @diegosiac.
 *
 * Va como parte de `redactText` a propósito: así queda cubierto en TODOS los
 * destinos que ya pasan por aquí (`request.url`, `Referer`, breadcrumbs,
 * `contexts`, `extra`, títulos) sin tener que acordarse de cada uno.
 *
 * El valor se corta en `&`, `#` o espacio para no comerse el resto de la URL.
 *
 * Incluye los parámetros de las URLs prefirmadas de S3 (SigV4): `X-Amz-Signature`
 * autoriza la descarga mientras no expire, `X-Amz-Security-Token` es la sesión
 * STS y `X-Amz-Credential` lleva el access key id. Los demás (`X-Amz-Date`,
 * `X-Amz-Expires`, `X-Amz-SignedHeaders`) se conservan: sirven para depurar
 * expiraciones y no dan acceso por sí solos.
 */
const SECRET_PARAM_RE =
  /([?&#](?:access_token|refresh_token|id_token|token|api_key|apikey|password|passwd|secret|authorization|auth|x-amz-signature|x-amz-security-token|x-amz-credential)=)[^&#\s"'<>]+/gi;

/** Mínimo de dígitos reales para considerar que un candidato es un teléfono. */
const MIN_PHONE_DIGITS = 7;

const countDigits = (s) => (s.match(/\d/g) || []).length;

export const normalizeKey = (key) =>
  String(key)
    .normalize("NFD")
    // Rango de diacríticos combinantes: `teléfono` → `telefono`.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Parte una clave en las palabras que la componen: límites camelCase más los
 * separadores habituales (`_`, `-`, `.`, espacios).
 *
 *   `messageText`  → ["message", "text"]
 *   `HTTPBody`     → ["http", "body"]
 *   `last_name`    → ["last", "name"]
 *   `filename`     → ["filename"]   ← una sola palabra, a propósito
 *
 * @param {string} key
 * @returns {string[]}
 */
export function splitKeyWords(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map(normalizeKey);
}

/**
 * ¿El nombre de esta clave anuncia PII?
 *
 * Dos reglas, no una: subcadena para las partes inequívocas y palabra completa
 * (con plural opcional) para las cuatro que aparecen dentro de palabras
 * corrientes. Ver el comentario de `SENSITIVE_KEY_WORDS`.
 *
 * @param {string} key
 * @returns {boolean}
 */
export function isSensitiveKey(key) {
  const k = normalizeKey(key);
  if (SENSITIVE_KEY_PARTS.some((part) => k.includes(part))) return true;

  if (TECHNICAL_KEY_EXACT.has(k)) return false;

  const words = splitKeyWords(key);
  return SENSITIVE_KEY_WORDS.some((word) =>
    words.some((w) => w === word || w === `${word}s`)
  );
}

/**
 * Redacta teléfonos, emails y secretos-en-URL dentro de un string, dejando el
 * resto del texto intacto. El conteo de dígitos se valida en el replacer y no
 * en la regex, porque `1 . . . . 2` matchea la forma pero no es un teléfono.
 *
 * El orden importa: los secretos de URL van PRIMERO para que el valor del
 * token desaparezca antes de que los otros dos patrones lo muerdan a trozos y
 * dejen medio JWT legible.
 *
 * @param {string} value
 * @returns {string}
 */
export function redactText(value) {
  if (typeof value !== "string" || value.length === 0) return value;

  let out = value.replace(SECRET_PARAM_RE, `$1${REDACTED}`);
  out = out.replace(EMAIL_RE, REDACTED);
  out = out.replace(PHONE_CANDIDATE_RE, (match, prefix, candidate) =>
    countDigits(candidate) >= MIN_PHONE_DIGITS ? `${prefix}${REDACTED}` : match
  );
  return out;
}

/**
 * ¿Esta clave de objeto es en sí misma un teléfono? Cubre mapas indexados por
 * número, donde el dato sensible es la clave y no el valor.
 * @param {string} key
 * @returns {boolean}
 */
export function keyLooksLikePhone(key) {
  const k = String(key);
  return countDigits(k) >= MIN_PHONE_DIGITS && /^[+\d\s().-]+$/.test(k);
}

/**
 * Barrido recursivo. Devuelve una copia redactada; NUNCA muta la entrada
 * (el state de Redux se pasa por aquí y mutarlo corrompería la app).
 *
 * @param {*} value      valor a sanear
 * @param {Object} [opts]
 * @param {number} [opts.maxNodes=MAX_NODES]
 * @param {number} [opts.maxDepth=MAX_DEPTH]
 * @param {boolean} [opts.technicalRoots=false] exime del barrido las claves de
 *   `TECHNICAL_ROOTS` en el nivel 0. SOLO para `event.contexts`.
 * @returns {*} copia saneada
 */
export function deepScrub(value, opts = {}) {
  const maxNodes = opts.maxNodes || MAX_NODES;
  const maxDepth = opts.maxDepth || MAX_DEPTH;
  // Sin este flag, la exención se aplicaba en el nivel 0 de CUALQUIER llamada,
  // no solo la de `contexts`. Desde que `request.data` pasa por aquí eso era un
  // hueco real: `{ device: { phone: "…" } }` en el body de un POST salía
  // intacto solo por llamarse `device`. Lo señaló @diegosiac en la revisión del
  // PR #230.
  const allowTechnicalRoots = opts.technicalRoots === true;
  const seen = new WeakSet();
  let nodes = 0;

  const walk = (node, depth, rootKey) => {
    nodes += 1;
    if (nodes > maxNodes || depth > maxDepth) return TRUNCATED;

    if (node === null || node === undefined) return node;

    if (typeof node === "string") return redactText(node);
    if (typeof node !== "object") return node; // number, boolean, bigint, symbol

    if (seen.has(node)) return TRUNCATED; // ciclo
    seen.add(node);

    if (Array.isArray(node)) {
      return node.map((item) => walk(item, depth + 1, rootKey));
    }

    // Objetos no planos (Date, Error, Map…): no los recorremos, los
    // representamos por su forma. `Date` es seguro y útil en diagnóstico.
    if (node instanceof Date) return node;

    const out = {};
    Object.keys(node).forEach((key) => {
      const path = rootKey === undefined ? key : rootKey;

      // Metadatos técnicos de Sentry: pasan intactos (solo en `contexts`).
      if (allowTechnicalRoots && rootKey === undefined && TECHNICAL_ROOTS.has(key)) {
        out[key] = node[key];
        return;
      }

      if (keyLooksLikePhone(key)) {
        out[REDACTED] = REDACTED;
        return;
      }
      if (isSensitiveKey(key)) {
        const child = node[key];
        // Números y booleanos bajo una clave sensible NO son PII y sí son
        // diagnóstico (`messagesCount: 12`, `hasPhone: true`). Todo lo demás
        // —strings, objetos, arrays— se borra entero sin mirarlo: es lo que
        // cubre el teléfono partido entre varios campos
        // (`phone: { parte1, parte2 }`), donde ninguna parte suelta llega al
        // mínimo de dígitos y el patrón por sí solo no lo atraparía.
        out[key] =
          typeof child === "number" || typeof child === "boolean" ? child : REDACTED;
        return;
      }
      out[key] = walk(node[key], depth + 1, path);
    });
    return out;
  };

  return walk(value, 0, undefined);
}
